import * as signalR from "@microsoft/signalr";
import { actionTypes } from "app/modules/auth";
import { container } from "setup/dependency/inversify.config";
import { ISessionService } from "services/SessionService";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { setAPIUnavailable } from "app/system/apiStatus/APIStatusSlice";
import { notifyAllActiveProcesses } from "app/modules/backgroundActivities/components/BackgroundActivitySlice";
import { setupCommonSignalRHandlers, setupSignalRServiceHandlers } from "./notifications/SignalRNotificationHelpers";

/* eslint @typescript-eslint/no-explicit-any: 0 */

let connection: signalR.HubConnection;
let initialised: boolean = false;
let reconnectIntervalID: ReturnType<typeof setInterval> | null = null;

const onConnected = async (dispatch: (arg0: any) => any, userID: string) => {
  try {
    await connection.invoke("Authenticate", userID);
    dispatch(setAPIUnavailable(false));
    dispatch(notifyAllActiveProcesses());
  } catch {
    dispatch(setAPIUnavailable(true));
    setTimeout(() => onConnected(dispatch, userID), RECONNECT_INTERVAL);
  }
};

const RECONNECT_INTERVAL = 1000 * 10; // 10 seconds

const startSignalRConnection = async (dispatch: (arg0: any) => any, userID: string) => {
  try {
    await connection.start();
    if (reconnectIntervalID != null) {
      clearInterval(reconnectIntervalID);
      reconnectIntervalID = null;
    }
    return await onConnected(dispatch, userID);
  } catch {
    dispatch(setAPIUnavailable(true));
    if (reconnectIntervalID == null) {
      reconnectIntervalID = setInterval(startSignalRConnection, RECONNECT_INTERVAL, dispatch, userID);
    }
  }
};

const cpl3SignalRMiddleware =
  ({ dispatch }: { dispatch: (arg0: any) => any }) =>
  (next: (arg0: { type: string }) => any) =>
  (action: { type: string; payload: any }) => {
    if (action.type === actionTypes.SetUser && !initialised) {
      const protocol = new signalR.JsonHubProtocol();
      const transport = signalR.HttpTransportType.LongPolling;
      const userID = action.payload.user.userID;

      const sessionService = container.get<ISessionService>(SERVICE_TYPES.SessionService);
      const options: signalR.IHttpConnectionOptions = {
        transport,
        logMessageContent: true,
        logger: signalR.LogLevel.Error,
        accessTokenFactory: async () => {
          return await sessionService.getCPLSessionToken();
        },
        headers: {
          Timeout: "-1",
        },
        withCredentials: false,
      };

      connection = new signalR.HubConnectionBuilder()
        .withUrl(`${process.env.REACT_APP_CPL_API_BASE_URI}/Notifications`, options)
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            const schedule = [0, 2000, 5000];
            return schedule[retryContext.previousRetryCount] ?? RECONNECT_INTERVAL;
          },
        })
        .withHubProtocol(protocol)
        .build();

      initialised = true;

      setupCommonSignalRHandlers(connection, dispatch);
      setupSignalRServiceHandlers(connection, dispatch);

      connection.onreconnected(() => {
        onConnected(dispatch, userID);
      });

      connection.onreconnecting(() => {
        dispatch(setAPIUnavailable(true));
      });

      startSignalRConnection(dispatch, userID);
    }

    if (action.type === actionTypes.Logout && initialised) {
      initialised = false;
      if (reconnectIntervalID != null) {
        clearInterval(reconnectIntervalID);
        reconnectIntervalID = null;
      }
      connection.stop();
    }

    return next(action);
  };

export default cpl3SignalRMiddleware;
